import { useEffect, useState } from "react";
import { Plus, Minus } from "lucide-react";

/**
 * Accordion FAQ block — visible UI + FAQPage JSON-LD for Google rich snippets.
 * id prop ensures the injected schema is removable on unmount.
 */
export default function FAQ({ items = [], id = "faq-jsonld", title = "Frequently asked questions", eyebrow = "FAQ" }) {
  const [open, setOpen] = useState(0);

  useEffect(() => {
    if (!items?.length) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [items, id]);

  if (!items?.length) return null;

  return (
    <section className="py-24 bg-ink-950" data-testid="faq-section">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="eyebrow gold-bar">{eyebrow}</div>
        <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight">{title}</h2>
        <div className="mt-12 divide-y divide-white/5 border-y border-white/5">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="py-1" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display text-lg md:text-xl text-white group-hover:text-gold transition-colors pr-4">
                    {item.q}
                  </span>
                  <span className={`shrink-0 w-9 h-9 rounded-full border ${isOpen ? "border-gold text-gold" : "border-white/15 text-white/60"} flex items-center justify-center transition-colors`}>
                    {isOpen ? <Minus size={16}/> : <Plus size={16}/>}
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 pb-7" : "max-h-0"}`}
                >
                  <p className="text-white/65 text-base leading-relaxed max-w-3xl">{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
